// Create room (§9.2, §9.4): every private-room setting, then `createRoom`. Opened from the lobby
// by the host with `{ edit: true }`, the same panel edits the live room (`settings`, LOBBY only).
import { Difficulty } from '@shared/ids';
import { DEFAULT_ROOM_SETTINGS, type RoomSettings } from '@shared/protocol/messages';
import type { LiveScreen } from '../contracts';
import type { UiDeps } from '../deps';
import { DIFFICULTY_NAMES } from '../labels';
import { el, setHidden, setText } from '../ui';
import { button, choice, closeButton, field, setLabel, stepper, toggle } from '../widgets';
import { mapPicker } from './pickers';

export interface CreateRoomProps { edit?: boolean }

export function create(deps: UiDeps): LiveScreen {
  let s: RoomSettings = { ...DEFAULT_ROOM_SETTINGS };
  let edit = false;
  const set = <K extends keyof RoomSettings>(k: K, v: RoomSettings[K]): void => {
    s = { ...s, [k]: v };
    sync();
  };

  const playlist = choice('Playlist', [
    { value: 'party' as const, label: 'Party Mix' }, { value: 'royale' as const, label: 'Royale' },
    { value: 'potato' as const, label: 'Hot Potato' }, { value: 'crown' as const, label: 'Crown Grab' },
  ], s.playlist, (v) => set('playlist', v));
  const map = mapPicker(s.map, (v) => set('map', v), true);
  const rounds = choice('Rounds', [{ value: 3 as const, label: '3' }, { value: 5 as const, label: '5' }, { value: 8 as const, label: '8' }], s.rounds, (v) => set('rounds', v));
  const golden = toggle('Golden Round', s.golden, (v) => set('golden', v));
  const items = toggle('Items', s.items, (v) => set('items', v));
  const botMode = choice('Bots', [{ value: 'fill', label: 'Fill to 8' }, { value: 'fixed', label: 'Fixed' }], s.botFill ? 'fill' : 'fixed', (v) => set('botFill', v === 'fill'));
  const botCount = stepper('bots', 0, 7, s.botCount, (n) => String(n), (v) => set('botCount', v));
  const difficulty = choice('Bot difficulty', [Difficulty.CHILL, Difficulty.NORMAL, Difficulty.SPICY].map((d) => ({ value: d, label: DIFFICULTY_NAMES[d] })), s.botDifficulty, (v) => set('botDifficulty', v));
  const teams = choice('Teams', [{ value: 'off' as const, label: 'Off' }, { value: 'split' as const, label: 'Split' }, { value: 'humansVsBots' as const, label: 'Humans vs Bots' }], s.teams, (v) => set('teams', v));
  const underdog = toggle("Underdog's Choice", s.underdog, (v) => set('underdog', v));
  const teamsNote = el('small', { class: 'field-note' });

  const ribbon = el('div', { class: 'ribbon sky' });
  const submit = button({ label: 'CREATE', icon: 'door', variant: 'pink', type: 'submit', autofocus: true });

  function sync(): void {
    setHidden(botCount.el, s.botFill);
    setText(teamsNote, s.teams !== 'off' && s.playlist === 'potato' ? 'Hot Potato is skipped with Teams on' : s.teams === 'humansVsBots' ? 'Everyone vs the bots!' : '');
  }

  function load(from: RoomSettings): void {
    s = { ...from };
    playlist.set(s.playlist, true);
    map.set(s.map);
    rounds.set(s.rounds, true);
    golden.set(s.golden, true);
    items.set(s.items, true);
    botMode.set(s.botFill ? 'fill' : 'fixed', true);
    botCount.set(s.botCount, true);
    difficulty.set(s.botDifficulty, true);
    teams.set(s.teams, true);
    underdog.set(s.underdog, true);
    sync();
  }

  const form = el('form', { class: 'panel sheet with-ribbon create-panel springy', attrs: { novalidate: true }, on: {
    submit: (e) => {
      e.preventDefault();
      if (edit) {
        deps.actions.send({ t: 'settings', settings: s });
        deps.nav.back();
      } else {
        deps.actions.createRoom(s);
      }
    },
  } },
    ribbon,
    closeButton(() => deps.nav.back()),
    el('div', { class: 'sheet-body' },
      el('div', { class: 'field-group' },
        el('h3', { class: 'group-title', text: 'What we play' }),
        field('Playlist', playlist.el),
        el('div', { class: 'field stacked' }, el('div', { class: 'flabel' }, el('span', { text: 'Map' })), map.el),
        field('Rounds', rounds.el, 'plus the Golden Round'),
        field('Golden Round', golden.el, 'Double points finale'),
        field('Items', items.el, 'Crates drop from the sky')),
      el('div', { class: 'field-group' },
        el('h3', { class: 'group-title', text: 'Who plays' }),
        field('Bots', el('div', { class: 'row wrap' }, botMode.el, botCount.el)),
        field('Bot difficulty', difficulty.el),
        field('Teams', el('div', { class: 'col' }, teams.el, teamsNote)),
        field("Underdog's Choice", underdog.el, 'Last place picks a mutator'))),
    el('div', { class: 'sheet-foot row center' },
      button({ label: 'BACK', icon: 'back', variant: 'cream', onClick: () => deps.nav.back() }), submit));

  return {
    el: el('div', { class: 'screen modal' }, form),
    onShow(props) {
      edit = (props as CreateRoomProps | undefined)?.edit === true;
      setText(ribbon, edit ? 'ROOM SETTINGS' : 'CREATE A ROOM');
      setLabel(submit, edit ? 'SAVE' : 'CREATE');
      load(edit && deps.room.dto ? deps.room.dto.settings : { ...DEFAULT_ROOM_SETTINGS, ...s });
    },
  };
}
